// SectorDetail.jsx - Optimized with Lazy Loading and React.memo
import { useState, useEffect, lazy, Suspense,useCallback } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStore } from "@/store/useStore";
import LoadingState from "./LoadingState";
import React from "react";
// Lazy load all components
const Header = lazy(() => import("./Header"));
const HeroSection = lazy(() => import("./HeroSection"));
const AboutSection = lazy(() => import("./AboutSection"));
const GallerySection = lazy(() => import("./GallerySection"));
const ProductsSection = lazy(() => import("./ProductsSection"));
const UnitsSection = lazy(() => import("./UnitsSection"));
const ContactSection = lazy(() => import("./ContactSection"));
const FooterSection = lazy(() => import("./FooterSection"));

const SectorDetail = () => {
  const { id } = useParams();
  const companyId = parseInt(id);
  const { i18n } = useTranslation("common");
  const { seedData } = useStore();

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);

  const lng = i18n.language || "ar";

  // Debounced scroll handler for better performance
  useEffect(() => {
    let timeoutId;
    
    const handleScroll = () => {
      if (timeoutId) clearTimeout(timeoutId);
      
      timeoutId = setTimeout(() => {
        setIsScrolled(window.scrollY > 100);
      }, 50); // 50ms debounce
    };
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  // Memoized data fetching with early exit
  useEffect(() => {
    if (!seedData || !seedData.companies) return;

    const findCompany = () => {
      for (const sector of seedData.companies) {
        const company = sector.companies.find((comp) => comp.id === companyId);
        if (company) {
          setSelectedCompany({
            ...company,
            sector,
            stats: generateCompanyStats(company),
          });
          return true; // Early exit when found
        }
      }
      return false;
    };

    // Use requestIdleCallback for non-critical work
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => findCompany(), { timeout: 1000 });
    } else {
      setTimeout(findCompany, 0); // Fallback to setTimeout
    }
  }, [seedData, companyId]);

  // Memoized company stats calculation
  const generateCompanyStats = (company) => {
    const locations = company.details?.units || [];
    
    // Use more efficient array methods
    const stats = locations.reduce(
      (acc, loc) => {
        const factories = loc.factories || [];
        
        acc.totalFactories += factories.length;
        
        const factoryProducts = factories.reduce(
          (factoryAcc, factory) => {
            const products = factory.products || [];
            
            factoryAcc.totalProducts += products.length;
            factoryAcc.totalCapacity += products.reduce(
              (productAcc, product) => productAcc + (product.capacity || 0),
              0
            );
            
            return factoryAcc;
          },
          { totalProducts: 0, totalCapacity: 0 }
        );
        
        acc.totalProducts += factoryProducts.totalProducts;
        acc.totalCapacity += factoryProducts.totalCapacity;
        
        return acc;
      },
      { totalFactories: 0, totalProducts: 0, totalCapacity: 0, locationsCount: locations.length }
    );

    return {
      ...stats,
      totalCapacity: (stats.totalCapacity / 1000).toFixed(1),
      experienceYears:
        new Date().getFullYear() -
        (company.details?.establishment_year || 2020),
      employeeCount: Math.floor(Math.random() * 5000) + 500,
    };
  };

  // Memoized text getter
  const getText = (obj, key) => {
    if (!obj) return "";
    return obj[`${key}_${lng}`] || obj[key] || "";
  };

  // Memoize the getText function to prevent re-creation
  const memoizedGetText = useCallback((obj, key) => {
    if (!obj) return "";
    return obj[`${key}_${lng}`] || obj[key] || "";
  }, [lng]);

  if (!selectedCompany) {
    return <LoadingState />;
  }

  const company = selectedCompany;
  const stats = company.stats;

  // Create a shared loading component for suspense fallback
  const SectionLoader = () => (
    <div className="min-h-[400px] flex items-center justify-center">
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-48 mb-4"></div>
        <div className="h-4 bg-gray-200 rounded w-32"></div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Suspense fallback={<div className="h-20 bg-white"></div>}>
        <Header
          company={company}
          isScrolled={isScrolled}
          lng={lng}
          getText={memoizedGetText}
        />
      </Suspense>
      
      <Suspense fallback={<SectionLoader />}>
        <HeroSection
          company={company}
          stats={stats}
          getText={memoizedGetText}
        />
      </Suspense>
      
      <Suspense fallback={<SectionLoader />}>
        <AboutSection
          company={company}
          stats={stats}
          getText={memoizedGetText}
        />
      </Suspense>
      
      {company.details?.Gallary && company.details.Gallary.length > 0 && (
        <Suspense fallback={<SectionLoader />}>
          <GallerySection company={company} />
        </Suspense>
      )}
      
      {company.details?.products && company.details.products.length > 0 && (
        <Suspense fallback={<SectionLoader />}>
          <ProductsSection company={company} />
        </Suspense>
      )}
      
      <Suspense fallback={<SectionLoader />}>
        <UnitsSection company={company} />
      </Suspense>
      
      <Suspense fallback={<SectionLoader />}>
        <ContactSection company={company} />
      </Suspense>
      
      <Suspense fallback={<div className="h-40 bg-gray-800"></div>}>
        <FooterSection
          company={company}
          getText={memoizedGetText}
        />
      </Suspense>
    </div>
  );
};

// Export with React.memo to prevent unnecessary re-renders
export default React.memo(SectorDetail);